import { UserProperties } from '@futrli-sdk/users';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { ERROR_STATUS, withErrorBoundary, WithErrorBoundaryProps } from '@futrli/errors';
import {
  getDataFromAPIMiddlewareAction,
  getOnboardingUrl,
  getOrganisationDetailUrl,
  getOrganisationsSettingsUrl,
  getOrganisationsUrl,
  getParamFromRoute,
  getPaymentDetailsPageUrl,
  getAllWorkspacesData,
  matchOrgDetailPath,
  matchOrgProductPath,
  matchOrgsPath,
  matchPaymentDetailsPagePath,
  matchSettingsPath,
  QUERY_PARAMS,
  ROUTE_PARAMS,
  workspacesSelector,
  PREDICT_PRODUCT_SLUG,
  getOnboardingByProductUrl,
  API_PATHS,
  matchConsolidationsRoutePath,
  organisationIdsByWorkspacesSelector,
  matchOnboardingRoute,
  getSageIdAdditionalInfoUrl,
  isSageIDUser,
  isSetupCompleted,
  isPureSFAWorkspace
} from '@futrli/shared-services';
import { UIDefaultErrorMessage, UILoader } from '@futrli/ui-components';
import { findProperty } from '@futrli/users';
import { ensureUserPropertiesData } from '@futrli/views/queries/userProperties';
import { get, isEmpty } from 'lodash';
import queryString, { StringifiableRecord } from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation, useParams, NavigateFunction, Location } from 'react-router-dom';
import { OrgsIdsByWorkspace, StoreState } from '../../core/redux-utils/types';
import { getCurrentUserId } from '../../utils/user';
import { WorkspaceContainer } from './workspace-container';
import { InitialOnboardingLoader } from '../../onboarding/components/initial-onboarding-nav';
import { useSxpContext } from '@futrli/predict';
import { isSXPManagedUser } from '@futrli/shared-services/authenticator/auth0/utils';
import { ensureIntegrationsData } from '@futrli/views/queries/integrations';

interface RouterProps {
  params: Record<string, string | undefined>;
  navigate: NavigateFunction;
  location: Location;
}

interface StateToProps {
  readonly sageIdUser: boolean;
  readonly SXPManagedUser: boolean;
  readonly setupCompleted: boolean;
  readonly workspaces: WorkspacesListingItem[] | null;
  readonly orgsIdsByWorkspace: Record<string, string[]>;
}

interface DispatchToProps {
  readonly getAllWorkspacesData: typeof getAllWorkspacesData;
}

type Props = RouterProps & StateToProps & DispatchToProps & WithErrorBoundaryProps & { sbcBaseUrl?: string };

interface State {
  isLoading: boolean;
  currentUserId: string | null;
}

export class WorkspacesContainerBase extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      currentUserId: null
    };
  }

  public componentDidMount() {
    const { sageIdUser, setupCompleted, navigate } = this.props;
    if (sageIdUser && !setupCompleted) {
      navigate(getSageIdAdditionalInfoUrl());
      return;
    }
    this.loadData();
  }

  public componentDidUpdate(prevProps: Props) {
    const prevWorkspaceId = getParamFromRoute(prevProps.params, ROUTE_PARAMS.WORKSPACE_ID);
    const currentWorkspaceId = getParamFromRoute(this.props.params, ROUTE_PARAMS.WORKSPACE_ID);

    if (currentWorkspaceId !== prevWorkspaceId) {
      this.handleRedirect();
    }
  }

  /* eslint-disable complexity */
  public render(): JSX.Element {
    const { params, location } = this.props;
    const { isLoading } = this.state;
    const workspaceId = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID);
    const searchParams = new URLSearchParams(location.search);
    const onboardingForecastId = searchParams.get(QUERY_PARAMS.ONBOARDING_FORECAST_ID);

    const isOnboardingRoute = !!matchOnboardingRoute(location.pathname);
    if (isLoading) {
      if (onboardingForecastId || isOnboardingRoute) {
        return <InitialOnboardingLoader />;
      }
      return <UILoader isFull={true} />;
    }

    if (workspaceId) {
      return <WorkspaceContainer {...this.props} />;
    }

    return <UIDefaultErrorMessage />;
  }

  private redirectTo(url: string, queryParams?: StringifiableRecord): void {
    const URL = queryParams ? `${url}?${queryString.stringify(queryParams)}` : url;

    this.props.navigate(URL);
  }

  private getOrgsCountByWorkspace(orgsIdsByWorkspace: OrgsIdsByWorkspace, workspaceId: string): number {
    return orgsIdsByWorkspace[workspaceId].length;
  }

  private async loadData() {
    const {
      sageIdUser,
      navigate,
      getAllWorkspacesData: getWorkspacesFn
    } = this.props;

    try {
      this.setState({ isLoading: true });

      const respondedWorkspaces = await getWorkspacesFn();
      const workspaceIds = getDataFromAPIMiddlewareAction(respondedWorkspaces);

      if (isEmpty(workspaceIds)) {
        // If workspacesIds is empty array, it needs to redirect users to additional info page where initial workspace is created
        if (sageIdUser) {
          return navigate(getSageIdAdditionalInfoUrl());
        }
        return navigate(getOnboardingUrl());
      } else {
        await ensureIntegrationsData();
        const currentUserId = await getCurrentUserId();
        this.setState(
          {
            currentUserId
          },
          () => {
            this.handleRedirect();
          }
        );
      }
    } catch (error) {
      const path = (get(error, 'response.config.url') as unknown) as string;
      const errorStatus = get(error, 'response.status');
      if (errorStatus === ERROR_STATUS._404 && path.startsWith(`${API_PATHS.ALL_WORKSPACES_DATA}`)) {
        if (sageIdUser) {
          // 404 means there aren't any workspaces for user. User needs to create initial workspace and that is
          // happening by calling workspace provision route on additional info page
          navigate(getSageIdAdditionalInfoUrl());
        } else {
          navigate(getOnboardingByProductUrl(PREDICT_PRODUCT_SLUG));
        }
      } else {
        this.props.onManageError(error);
      }
    }
  }

  /**
   * Handles the main redirection checking the user properties first. If user does not
   * have to be redirected by any of these ones, then call the handleRedirectByWorkspaceId method
   * which takes into account the skeleton navigation rules.
   */
  private async handleRedirect() {
    const { location, params, workspaces } = this.props;
    const { currentUserId } = this.state;
    const workspaceId = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID);

    if (workspaceId && workspaces) {
      const workspace = workspaces.find(w => w.id === workspaceId);
      const isOwner = workspace?.owner.id === currentUserId;

      if (isOwner && !matchPaymentDetailsPagePath(location.pathname)) {
        const userProperties = await ensureUserPropertiesData();
        const redirectToPaymentDetails =
          findProperty(userProperties, UserProperties.redirect_to_payment_details)?.value === 'true';

        if (redirectToPaymentDetails) {
          this.redirectTo(getPaymentDetailsPageUrl(workspaceId));
          return this.setState({ isLoading: false });
        }
      }
    }

    this.handleRedirectByWorkspaceId(workspaceId);
  }

  /**
   * Handles the redirection if needed for an specific workspace taking into account
   * the skeleton rules.
   * @param {string|undefined} workspaceId
   */
  private handleRedirectByWorkspaceId(workspaceId: string | undefined) {
    if (!workspaceId) {
      this.setState({ isLoading: false });
      return;
    }
    this.setState({ isLoading: true });
    const { location, orgsIdsByWorkspace, workspaces, sbcBaseUrl, SXPManagedUser, params } = this.props;
    const { currentUserId } = this.state;
    const organisationId = getParamFromRoute(params, ROUTE_PARAMS.ORGANISATION_ID);
    const ownedWorkspaces = (workspaces || []).filter(w => w.owner.id === currentUserId);
    if (
      SXPManagedUser &&
      !organisationId &&
      !location.pathname.toLowerCase().includes('consolidations') &&
      sbcBaseUrl
    ) {
      const workspaceToCheck =
        workspaces?.find(workspace => workspace.id === workspaceId) ||
        ownedWorkspaces[0] ||
        workspaces![0];
      if (workspaceToCheck && (workspaceToCheck.organisations.length === 0 || isPureSFAWorkspace(workspaceToCheck))) {
        window.location.href = sbcBaseUrl;
        return;
      }
    }

    if (!orgsIdsByWorkspace || !this.needsRedirect()) {
      this.setState({ isLoading: false });
      return;
    }

    const workspaceIdToGo = workspaceId;
    const orgsCountByWorkspace = this.getOrgsCountByWorkspace(orgsIdsByWorkspace, workspaceIdToGo);

    if (!orgsCountByWorkspace) {
      this.redirectTo(getOrganisationsSettingsUrl(workspaceIdToGo), {
        [QUERY_PARAMS.ADD_NEW_ORG]: true
      });
    } else if (orgsCountByWorkspace === 1) {
      const firstOrg = orgsIdsByWorkspace[workspaceIdToGo][0];

      this.redirectTo(getOrganisationDetailUrl(workspaceIdToGo, firstOrg));
    } else if (!matchOrgsPath(location.pathname) && !matchConsolidationsRoutePath(location.pathname)) {
      this.redirectTo(getOrganisationsUrl(workspaceIdToGo));
    }
    this.setState({ isLoading: false });
  }

  private needsRedirect() {
    const {
      location: { pathname }
    } = this.props;

    if (
      matchSettingsPath(pathname) ||
      matchOrgProductPath(pathname) ||
      matchOrgDetailPath(pathname) ||
      matchPaymentDetailsPagePath(pathname)
    ) {
      return false;
    }

    return true;
  }
}

// Redux
const mapStateToProps = ({ normalised, authReducers }: StoreState): StateToProps => {
  const workspaces = workspacesSelector(normalised) as WorkspacesListingItem[] | null;
  const workspaceIds = workspaces?.map(w => w.id);
  const orgsIdsByWorkspace = workspaceIds ? organisationIdsByWorkspacesSelector(normalised, workspaceIds) : {};

  return {
    sageIdUser: isSageIDUser(authReducers.authenticatedUser),
    SXPManagedUser: isSXPManagedUser(authReducers.authenticatedUser),
    setupCompleted: isSetupCompleted(authReducers.authenticatedUser),
    workspaces,
    orgsIdsByWorkspace
  };
};

const mapDispatchToProps: DispatchToProps = {
  getAllWorkspacesData,
};

export const WorkspacesContainerBaseWE = withErrorBoundary(WorkspacesContainerBase);
export const WorkspacesContainerWS = connect(mapStateToProps, mapDispatchToProps)(WorkspacesContainerBaseWE);

export const WorkspacesContainer: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sbcBaseUrl } = useSxpContext();

  // Extract organisationId and consolidationId from pathname since they're in child routes
  const organisationMatch = /\/organisations\/([^/]+)/.exec(location.pathname);
  const consolidationMatch = /\/consolidations\/([^/]+)/.exec(location.pathname);

  const enhancedParams = {
    ...params,
    ...(organisationMatch && { [ROUTE_PARAMS.ORGANISATION_ID]: organisationMatch[1] }),
    ...(consolidationMatch && { [ROUTE_PARAMS.ORGANISATION_ID]: consolidationMatch[1] }) // consolidations use ORGANISATION_ID param
  };

  return (
    <WorkspacesContainerWS
      params={enhancedParams}
      navigate={navigate as NavigateFunction}
      location={location}
      sbcBaseUrl={sbcBaseUrl}
    />
  );
};
